var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

// src/html-serializer.ts
async function lazyParse(markdown) {
  try {
    const core = await import("@create-markdown/core");
    return core.parse(markdown);
  } catch {
    throw new Error(
      "@create-markdown/core is required for markdownToHTML(). Install it as a dependency, or use applyPreviewTheme() with your own parser instead."
    );
  }
}
var DEFAULT_OPTIONS = {
  classPrefix: "cm-",
  theme: "github",
  linkTarget: "_blank",
  sanitize: false,
  plugins: [],
  customRenderers: {}
};
function resolveOptions(options) {
  return {
    ...DEFAULT_OPTIONS,
    ...options,
    plugins: options?.plugins ?? [],
    customRenderers: options?.customRenderers ?? {}
  };
}
function applySanitize(html, sanitize) {
  if (typeof sanitize === "function") {
    return sanitize(html);
  }
  return html;
}
function blocksToHTML(blocks, options) {
  const opts = resolveOptions(options);
  const prefix = opts.classPrefix;
  let transformedBlocks = blocks;
  for (const plugin of opts.plugins) {
    if (plugin.transformBlock) {
      transformedBlocks = transformedBlocks.map(plugin.transformBlock);
    }
  }
  const htmlParts = transformedBlocks.map((block) => {
    for (const plugin of opts.plugins) {
      if (plugin.renderBlock) {
        const result = plugin.renderBlock(block, () => renderBlock(block, opts));
        if (result !== null) {
          return result;
        }
      }
    }
    const customRenderer = opts.customRenderers[block.type];
    if (customRenderer) {
      return customRenderer(block);
    }
    return renderBlock(block, opts);
  });
  let html = `<div class="${prefix}preview">${htmlParts.join("\n")}</div>`;
  html = applySanitize(html, opts.sanitize);
  return html;
}
async function markdownToHTML(markdown, options) {
  const blocks = await lazyParse(markdown);
  return blocksToHTML(blocks, options);
}
async function renderAsync(blocks, options) {
  const opts = resolveOptions(options);
  for (const plugin of opts.plugins) {
    if (plugin.init) {
      await plugin.init();
    }
  }
  let html = blocksToHTML(blocks, options);
  for (const plugin of opts.plugins) {
    if (plugin.postProcess) {
      html = await plugin.postProcess(html);
    }
  }
  return html;
}
function renderBlock(block, opts) {
  const prefix = opts.classPrefix;
  switch (block.type) {
    case "paragraph":
      return `<p class="${prefix}paragraph">${renderInlineContent(block.content, opts)}</p>`;
    case "heading":
      return renderHeading(block, opts);
    case "bulletList":
      return renderBulletList(block, opts);
    case "numberedList":
      return renderNumberedList(block, opts);
    case "checkList":
      return renderCheckList(block, opts);
    case "codeBlock":
      return renderCodeBlock(block, opts);
    case "blockquote":
      return `<blockquote class="${prefix}blockquote">${renderInlineContent(block.content, opts)}</blockquote>`;
    case "table":
      return renderTable(block, opts);
    case "image":
      return renderImage(block, opts);
    case "divider":
      return `<hr class="${prefix}divider" />`;
    case "callout":
      return renderCallout(block, opts);
    default:
      return `<div class="${prefix}unknown">${renderInlineContent(block.content, opts)}</div>`;
  }
}
function renderHeading(block, opts) {
  const prefix = opts.classPrefix;
  const level = block.props.level;
  const tag = `h${level}`;
  const content = renderInlineContent(block.content, opts);
  return `<${tag} class="${prefix}heading ${prefix}h${level}">${content}</${tag}>`;
}
function renderBulletList(block, opts) {
  const prefix = opts.classPrefix;
  const items = block.children.map((child) => `<li>${renderInlineContent(child.content, opts)}</li>`).join("\n");
  return `<ul class="${prefix}bullet-list">
${items}
</ul>`;
}
function renderNumberedList(block, opts) {
  const prefix = opts.classPrefix;
  const items = block.children.map((child) => `<li>${renderInlineContent(child.content, opts)}</li>`).join("\n");
  return `<ol class="${prefix}numbered-list">
${items}
</ol>`;
}
function renderCheckList(block, opts) {
  const prefix = opts.classPrefix;
  const checked = block.props.checked;
  const checkboxAttr = checked ? "checked disabled" : "disabled";
  const textClass = checked ? `${prefix}checked` : "";
  return `
<div class="${prefix}checklist-item">
  <input type="checkbox" ${checkboxAttr} />
  <span class="${textClass}">${renderInlineContent(block.content, opts)}</span>
</div>`.trim();
}
function renderCodeBlock(block, opts) {
  const prefix = opts.classPrefix;
  const code = block.content.map((span) => span.text).join("");
  const language = block.props.language || "";
  const escapedCode = escapeHtml(code);
  const langClass = language ? ` language-${language}` : "";
  const dataLang = language ? ` data-language="${language}"` : "";
  return `<pre class="${prefix}code-block"${dataLang}><code class="${prefix}code${langClass}">${escapedCode}</code></pre>`;
}
function renderTable(block, opts) {
  const prefix = opts.classPrefix;
  const { headers, rows, alignments } = block.props;
  const getStyle = (index) => {
    const align = alignments?.[index];
    return align ? ` style="text-align: ${align}"` : "";
  };
  const headerRow = headers.length > 0 ? `<thead><tr>${headers.map((h, i) => `<th${getStyle(i)}>${escapeHtml(h)}</th>`).join("")}</tr></thead>` : "";
  const bodyRows = rows.map((row) => `<tr>${row.map((cell, i) => `<td${getStyle(i)}>${escapeHtml(cell)}</td>`).join("")}</tr>`).join("\n");
  return `<table class="${prefix}table">
${headerRow}
<tbody>
${bodyRows}
</tbody>
</table>`;
}
function renderImage(block, opts) {
  const prefix = opts.classPrefix;
  const { url, alt, title, width, height } = block.props;
  const altAttr = alt ? ` alt="${escapeHtml(alt)}"` : ' alt=""';
  const titleAttr = title ? ` title="${escapeHtml(title)}"` : "";
  const widthAttr = width ? ` width="${width}"` : "";
  const heightAttr = height ? ` height="${height}"` : "";
  const img = `<img src="${escapeHtml(url)}"${altAttr}${titleAttr}${widthAttr}${heightAttr} />`;
  const caption = alt ? `<figcaption>${escapeHtml(alt)}</figcaption>` : "";
  return `<figure class="${prefix}image">${img}${caption}</figure>`;
}
function renderCallout(block, opts) {
  const prefix = opts.classPrefix;
  const type = block.props.type;
  const content = renderInlineContent(block.content, opts);
  return `
<div class="${prefix}callout ${prefix}callout-${type}" role="alert">
  <strong class="${prefix}callout-title">${type}</strong>
  <div class="${prefix}callout-content">${content}</div>
</div>`.trim();
}
function renderInlineContent(spans, opts) {
  return spans.map((span) => renderSpan(span, opts)).join("");
}
function renderSpan(span, opts) {
  let html = escapeHtml(span.text);
  const styles = span.styles;
  if (styles.code) {
    html = `<code>${html}</code>`;
  }
  if (styles.highlight) {
    html = `<mark>${html}</mark>`;
  }
  if (styles.strikethrough) {
    html = `<del>${html}</del>`;
  }
  if (styles.underline) {
    html = `<u>${html}</u>`;
  }
  if (styles.italic) {
    html = `<em>${html}</em>`;
  }
  if (styles.bold) {
    html = `<strong>${html}</strong>`;
  }
  if (styles.link) {
    const target = opts.linkTarget === "_blank" ? ' target="_blank" rel="noopener noreferrer"' : "";
    const title = styles.link.title ? ` title="${escapeHtml(styles.link.title)}"` : "";
    html = `<a href="${escapeHtml(styles.link.url)}"${title}${target}>${html}</a>`;
  }
  return html;
}
function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

// src/apply-theme.ts
function buildTagMappings(prefix) {
  const headings = [1, 2, 3, 4, 5, 6].map((level) => ({
    tag: `h${level}`,
    classes: [`${prefix}heading`, `${prefix}h${level}`]
  }));
  return [
    ...headings,
    { tag: "p", classes: [`${prefix}paragraph`] },
    { tag: "ul", classes: [`${prefix}bullet-list`] },
    { tag: "ol", classes: [`${prefix}numbered-list`] },
    { tag: "pre", classes: [`${prefix}code-block`] },
    { tag: "blockquote", classes: [`${prefix}blockquote`] },
    { tag: "hr", classes: [`${prefix}divider`] },
    { tag: "table", classes: [`${prefix}table`] },
    { tag: "figure", classes: [`${prefix}image`] }
  ];
}
function injectClasses(openTag, classes) {
  const classStr = classes.join(" ");
  const classAttrRe = /\bclass\s*=\s*"([^"]*)"/i;
  const match = openTag.match(classAttrRe);
  if (match) {
    return openTag.replace(classAttrRe, `class="${classStr} ${match[1]}"`);
  }
  if (openTag.endsWith("/>")) {
    return openTag.slice(0, -2) + ` class="${classStr}" />`;
  }
  return openTag.slice(0, -1) + ` class="${classStr}">`;
}
function wrapStandaloneImages(html, prefix) {
  return html.replace(
    /(?<!<figure[^>]*>\s*)(<img\s[^>]*\/?>)(?!\s*<\/figure>)/gi,
    `<figure class="${prefix}image">$1</figure>`
  );
}
function applyPreviewTheme(html, options) {
  const prefix = options?.classPrefix ?? "cm-";
  const wrapperClass = options?.wrapperClass ?? `${prefix}preview`;
  const mappings = buildTagMappings(prefix);
  let result = html;
  for (const { tag, classes } of mappings) {
    const fullTagRe = new RegExp(`<${tag}(\\s[^>]*)?>|<${tag}\\s*\\/?>`, "gi");
    result = result.replace(fullTagRe, (match) => injectClasses(match, classes));
  }
  result = wrapStandaloneImages(result, prefix);
  result = `<div class="${wrapperClass}">${result}</div>`;
  if (typeof options?.sanitize === "function") {
    result = options.sanitize(result);
  }
  return result;
}

// src/themes/css-strings.ts
var githubDark = `/**
 * @create-markdown/preview - GitHub Dark Theme
 * Dark theme inspired by GitHub's dark mode markdown rendering
 */

.cm-preview {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans', Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  color: #e6edf3;
  background-color: #0d1117;
  max-width: 100%;
  overflow-wrap: break-word;
}

/* Headings */
.cm-heading {
  margin-top: 24px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.25;
}

.cm-h1 {
  font-size: 2em;
  padding-bottom: 0.3em;
  border-bottom: 1px solid #30363d;
}

.cm-h2 {
  font-size: 1.5em;
  padding-bottom: 0.3em;
  border-bottom: 1px solid #30363d;
}

.cm-h3 { font-size: 1.25em; }
.cm-h4 { font-size: 1em; }
.cm-h5 { font-size: 0.875em; }
.cm-h6 { font-size: 0.85em; color: #8b949e; }

/* Paragraphs */
.cm-paragraph {
  margin-top: 0;
  margin-bottom: 16px;
}

/* Lists */
.cm-bullet-list,
.cm-numbered-list {
  margin-top: 0;
  margin-bottom: 16px;
  padding-left: 2em;
}

.cm-bullet-list li,
.cm-numbered-list li {
  margin-bottom: 4px;
}

/* Checklist */
.cm-checklist-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 4px;
}

.cm-checklist-item input[type="checkbox"] {
  margin-top: 0.25rem;
}

.cm-checked {
  text-decoration: line-through;
  color: #8b949e;
}

/* Code */
.cm-code-block {
  background-color: #161b22;
  border-radius: 6px;
  padding: 16px;
  overflow-x: auto;
  margin-bottom: 16px;
  font-size: 0.875em;
}

.cm-code {
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace;
  background: transparent;
  padding: 0;
  white-space: pre;
}

code {
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace;
  font-size: 0.875em;
  background-color: rgba(110, 118, 129, 0.4);
  padding: 0.2em 0.4em;
  border-radius: 6px;
}

/* Blockquote */
.cm-blockquote {
  margin: 0 0 16px 0;
  padding: 0 1em;
  color: #8b949e;
  border-left: 0.25em solid #30363d;
}

/* Divider */
.cm-divider {
  height: 0.25em;
  padding: 0;
  margin: 24px 0;
  background-color: #30363d;
  border: 0;
}

/* Table */
.cm-table {
  display: block;
  width: max-content;
  max-width: 100%;
  overflow: auto;
  border-spacing: 0;
  border-collapse: collapse;
  margin-bottom: 16px;
}

.cm-table th,
.cm-table td {
  padding: 6px 13px;
  border: 1px solid #30363d;
}

.cm-table th {
  font-weight: 600;
  background-color: #161b22;
}

.cm-table tr:nth-child(2n) {
  background-color: #161b22;
}

/* Image */
.cm-image {
  margin: 0 0 16px 0;
}

.cm-image img {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}

.cm-image figcaption {
  font-size: 0.875em;
  color: #8b949e;
  margin-top: 8px;
  text-align: center;
}

/* Callout */
.cm-callout {
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 6px;
  border-left: 4px solid;
}

.cm-callout-title {
  display: block;
  text-transform: capitalize;
  margin-bottom: 8px;
}

.cm-callout-info {
  border-left-color: #58a6ff;
  background-color: rgba(56, 139, 253, 0.15);
}

.cm-callout-warning {
  border-left-color: #d29922;
  background-color: rgba(187, 128, 9, 0.15);
}

.cm-callout-tip {
  border-left-color: #3fb950;
  background-color: rgba(46, 160, 67, 0.15);
}

.cm-callout-danger {
  border-left-color: #f85149;
  background-color: rgba(248, 81, 73, 0.15);
}

.cm-callout-note {
  border-left-color: #8b949e;
  background-color: rgba(110, 118, 129, 0.15);
}

/* Links */
.cm-preview a {
  color: #58a6ff;
  text-decoration: none;
}

.cm-preview a:hover {
  text-decoration: underline;
}

/* Inline styles */
.cm-preview mark {
  background-color: rgba(187, 128, 9, 0.4);
  padding: 0.1em 0.2em;
  border-radius: 3px;
}

.cm-preview del {
  color: #8b949e;
}
`;
var github = `/**
 * @create-markdown/preview - GitHub Theme
 * Light theme inspired by GitHub's markdown rendering
 */

.cm-preview {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans', Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  color: #1f2328;
  background-color: #ffffff;
  max-width: 100%;
  overflow-wrap: break-word;
}

/* Headings */
.cm-heading {
  margin-top: 24px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.25;
}

.cm-h1 {
  font-size: 2em;
  padding-bottom: 0.3em;
  border-bottom: 1px solid #d1d9e0;
}

.cm-h2 {
  font-size: 1.5em;
  padding-bottom: 0.3em;
  border-bottom: 1px solid #d1d9e0;
}

.cm-h3 { font-size: 1.25em; }
.cm-h4 { font-size: 1em; }
.cm-h5 { font-size: 0.875em; }
.cm-h6 { font-size: 0.85em; color: #656d76; }

/* Paragraphs */
.cm-paragraph {
  margin-top: 0;
  margin-bottom: 16px;
}

/* Lists */
.cm-bullet-list,
.cm-numbered-list {
  margin-top: 0;
  margin-bottom: 16px;
  padding-left: 2em;
}

.cm-bullet-list li,
.cm-numbered-list li {
  margin-bottom: 4px;
}

/* Checklist */
.cm-checklist-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 4px;
}

.cm-checklist-item input[type="checkbox"] {
  margin-top: 0.25rem;
}

.cm-checked {
  text-decoration: line-through;
  color: #656d76;
}

/* Code */
.cm-code-block {
  background-color: #f6f8fa;
  border-radius: 6px;
  padding: 16px;
  overflow-x: auto;
  margin-bottom: 16px;
  font-size: 0.875em;
}

.cm-code {
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace;
  background: transparent;
  padding: 0;
  white-space: pre;
}

code {
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace;
  font-size: 0.875em;
  background-color: rgba(175, 184, 193, 0.2);
  padding: 0.2em 0.4em;
  border-radius: 6px;
}

/* Blockquote */
.cm-blockquote {
  margin: 0 0 16px 0;
  padding: 0 1em;
  color: #656d76;
  border-left: 0.25em solid #d1d9e0;
}

/* Divider */
.cm-divider {
  height: 0.25em;
  padding: 0;
  margin: 24px 0;
  background-color: #d1d9e0;
  border: 0;
}

/* Table */
.cm-table {
  display: block;
  width: max-content;
  max-width: 100%;
  overflow: auto;
  border-spacing: 0;
  border-collapse: collapse;
  margin-bottom: 16px;
}

.cm-table th,
.cm-table td {
  padding: 6px 13px;
  border: 1px solid #d1d9e0;
}

.cm-table th {
  font-weight: 600;
  background-color: #f6f8fa;
}

.cm-table tr:nth-child(2n) {
  background-color: #f6f8fa;
}

/* Image */
.cm-image {
  margin: 0 0 16px 0;
}

.cm-image img {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}

.cm-image figcaption {
  font-size: 0.875em;
  color: #656d76;
  margin-top: 8px;
  text-align: center;
}

/* Callout */
.cm-callout {
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 6px;
  border-left: 4px solid;
}

.cm-callout-title {
  display: block;
  text-transform: capitalize;
  margin-bottom: 8px;
}

.cm-callout-info {
  border-left-color: #0969da;
  background-color: #ddf4ff;
}

.cm-callout-warning {
  border-left-color: #d4a72c;
  background-color: #fff8c5;
}

.cm-callout-tip {
  border-left-color: #1a7f37;
  background-color: #dafbe1;
}

.cm-callout-danger {
  border-left-color: #cf222e;
  background-color: #ffebe9;
}

.cm-callout-note {
  border-left-color: #656d76;
  background-color: #f6f8fa;
}

/* Links */
.cm-preview a {
  color: #0969da;
  text-decoration: none;
}

.cm-preview a:hover {
  text-decoration: underline;
}

/* Inline styles */
.cm-preview mark {
  background-color: #fff8c5;
  padding: 0.1em 0.2em;
  border-radius: 3px;
}

.cm-preview del {
  color: #656d76;
}
`;
var minimal = "/**\n * @create-markdown/preview - Minimal Theme\n * A clean, minimal theme with subtle styling\n */\n\n.cm-preview {\n  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;\n  font-size: 16px;\n  line-height: 1.7;\n  color: #333;\n  max-width: 100%;\n  overflow-wrap: break-word;\n}\n\n/* Headings */\n.cm-heading {\n  margin-top: 1.5em;\n  margin-bottom: 0.5em;\n  font-weight: 600;\n  line-height: 1.3;\n}\n\n.cm-h1 { font-size: 1.75em; }\n.cm-h2 { font-size: 1.5em; }\n.cm-h3 { font-size: 1.25em; }\n.cm-h4 { font-size: 1.1em; }\n.cm-h5 { font-size: 1em; }\n.cm-h6 { font-size: 0.9em; color: #666; }\n\n/* Paragraphs */\n.cm-paragraph {\n  margin-top: 0;\n  margin-bottom: 1em;\n}\n\n/* Lists */\n.cm-bullet-list,\n.cm-numbered-list {\n  margin-top: 0;\n  margin-bottom: 1em;\n  padding-left: 1.5em;\n}\n\n.cm-bullet-list li,\n.cm-numbered-list li {\n  margin-bottom: 0.25em;\n}\n\n/* Checklist */\n.cm-checklist-item {\n  display: flex;\n  align-items: flex-start;\n  gap: 0.5rem;\n  margin-bottom: 0.25em;\n}\n\n.cm-checked {\n  text-decoration: line-through;\n  opacity: 0.6;\n}\n\n/* Code */\n.cm-code-block {\n  background-color: #f5f5f5;\n  border-radius: 4px;\n  padding: 1em;\n  overflow-x: auto;\n  margin-bottom: 1em;\n  font-size: 0.9em;\n}\n\n.cm-code {\n  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', monospace;\n  background: transparent;\n  padding: 0;\n  white-space: pre;\n}\n\ncode {\n  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', monospace;\n  font-size: 0.9em;\n  background-color: #f0f0f0;\n  padding: 0.15em 0.3em;\n  border-radius: 3px;\n}\n\n/* Blockquote */\n.cm-blockquote {\n  margin: 0 0 1em 0;\n  padding: 0 1em;\n  color: #666;\n  border-left: 3px solid #ddd;\n  font-style: italic;\n}\n\n/* Divider */\n.cm-divider {\n  height: 1px;\n  padding: 0;\n  margin: 2em 0;\n  background-color: #eee;\n  border: 0;\n}\n\n/* Table */\n.cm-table {\n  display: block;\n  width: max-content;\n  max-width: 100%;\n  overflow: auto;\n  border-spacing: 0;\n  border-collapse: collapse;\n  margin-bottom: 1em;\n}\n\n.cm-table th,\n.cm-table td {\n  padding: 0.5em 1em;\n  border: 1px solid #ddd;\n}\n\n.cm-table th {\n  font-weight: 600;\n  background-color: #f9f9f9;\n}\n\n/* Image */\n.cm-image {\n  margin: 0 0 1em 0;\n}\n\n.cm-image img {\n  max-width: 100%;\n  height: auto;\n}\n\n.cm-image figcaption {\n  font-size: 0.875em;\n  color: #666;\n  margin-top: 0.5em;\n  text-align: center;\n}\n\n/* Callout */\n.cm-callout {\n  padding: 1em;\n  margin-bottom: 1em;\n  border-radius: 4px;\n  border-left: 3px solid;\n  background-color: #f9f9f9;\n}\n\n.cm-callout-title {\n  display: block;\n  text-transform: capitalize;\n  font-weight: 600;\n  margin-bottom: 0.5em;\n}\n\n.cm-callout-info { border-left-color: #3b82f6; }\n.cm-callout-warning { border-left-color: #f59e0b; }\n.cm-callout-tip { border-left-color: #10b981; }\n.cm-callout-danger { border-left-color: #ef4444; }\n.cm-callout-note { border-left-color: #6b7280; }\n\n/* Links */\n.cm-preview a {\n  color: #0066cc;\n  text-decoration: none;\n}\n\n.cm-preview a:hover {\n  text-decoration: underline;\n}\n\n/* Inline styles */\n.cm-preview mark {\n  background-color: #fff3cd;\n  padding: 0.1em 0.2em;\n  border-radius: 2px;\n}\n\n.cm-preview del {\n  opacity: 0.6;\n}\n";
var system = `/**
 * @create-markdown/preview - System Theme
 * CSS custom property theme that integrates with any design system.
 * Override --cm-* variables, or fall back to common --text / --bg / --border tokens.
 */

.cm-preview {
  font-family: var(--cm-font, inherit);
  font-size: var(--cm-font-size, 1rem);
  line-height: var(--cm-line-height, 1.6);
  color: var(--cm-text, var(--text, inherit));
  background: var(--cm-bg, var(--bg, transparent));
  max-width: 100%;
  overflow-wrap: break-word;
}

/* Headings */
.cm-heading {
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  font-weight: var(--cm-heading-weight, 600);
  line-height: 1.25;
}

.cm-h1 {
  font-size: 2em;
  padding-bottom: 0.3em;
  border-bottom: 1px solid var(--cm-border, var(--border, currentColor));
}

.cm-h2 {
  font-size: 1.5em;
  padding-bottom: 0.3em;
  border-bottom: 1px solid var(--cm-border, var(--border, currentColor));
}

.cm-h3 { font-size: 1.25em; }
.cm-h4 { font-size: 1em; }
.cm-h5 { font-size: 0.875em; }
.cm-h6 { font-size: 0.85em; color: var(--cm-muted, var(--muted, #656d76)); }

/* Paragraphs */
.cm-paragraph {
  margin-top: 0;
  margin-bottom: 1em;
}

/* Lists */
.cm-bullet-list,
.cm-numbered-list {
  margin-top: 0;
  margin-bottom: 1em;
  padding-left: 2em;
}

.cm-bullet-list li,
.cm-numbered-list li {
  margin-bottom: 0.25em;
}

/* Checklist */
.cm-checklist-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.25em;
}

.cm-checklist-item input[type="checkbox"] {
  margin-top: 0.25rem;
}

.cm-checked {
  text-decoration: line-through;
  color: var(--cm-muted, var(--muted, #656d76));
}

/* Code */
.cm-code-block {
  background: var(--cm-code-bg, var(--secondary, #f5f5f5));
  border: 1px solid var(--cm-border, var(--border, #ddd));
  border-radius: var(--cm-radius, 6px);
  padding: 1em;
  overflow-x: auto;
  margin-bottom: 1em;
  font-size: 0.875em;
}

.cm-code {
  font-family: var(--cm-mono, ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace);
  background: transparent;
  padding: 0;
  white-space: pre;
}

code {
  font-family: var(--cm-mono, ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace);
  font-size: 0.875em;
  background-color: var(--cm-inline-code-bg, var(--secondary, rgba(175, 184, 193, 0.2)));
  padding: 0.2em 0.4em;
  border-radius: var(--cm-radius, 6px);
}

/* Blockquote */
.cm-blockquote {
  margin: 0 0 1em 0;
  padding: 0 1em;
  color: var(--cm-muted, var(--muted, #656d76));
  border-left: 0.25em solid var(--cm-border, var(--border, #ddd));
}

/* Divider */
.cm-divider {
  height: 0.25em;
  padding: 0;
  margin: 1.5em 0;
  background-color: var(--cm-border, var(--border, #ddd));
  border: 0;
}

/* Table */
.cm-table {
  display: block;
  width: max-content;
  max-width: 100%;
  overflow: auto;
  border-spacing: 0;
  border-collapse: collapse;
  margin-bottom: 1em;
}

.cm-table th,
.cm-table td {
  padding: 6px 13px;
  border: 1px solid var(--cm-border, var(--border, #ddd));
}

.cm-table th {
  font-weight: 600;
  background-color: var(--cm-table-header-bg, var(--secondary, #f6f8fa));
}

.cm-table tr:nth-child(2n) {
  background-color: var(--cm-table-stripe-bg, var(--secondary, #f6f8fa));
}

/* Image */
.cm-image {
  margin: 0 0 1em 0;
}

.cm-image img {
  max-width: 100%;
  height: auto;
  border-radius: var(--cm-radius, 6px);
}

.cm-image figcaption {
  font-size: 0.875em;
  color: var(--cm-muted, var(--muted, #656d76));
  margin-top: 0.5em;
  text-align: center;
}

/* Callout */
.cm-callout {
  padding: 1em;
  margin-bottom: 1em;
  border-radius: var(--cm-radius, 6px);
  border-left: 4px solid;
  background-color: var(--cm-callout-bg, var(--secondary, #f6f8fa));
}

.cm-callout-title {
  display: block;
  text-transform: capitalize;
  font-weight: 600;
  margin-bottom: 0.5em;
}

.cm-callout-info { border-left-color: var(--cm-info, #0969da); }
.cm-callout-warning { border-left-color: var(--cm-warning, #d4a72c); }
.cm-callout-tip { border-left-color: var(--cm-success, #1a7f37); }
.cm-callout-danger { border-left-color: var(--cm-danger, #cf222e); }
.cm-callout-note { border-left-color: var(--cm-muted, var(--muted, #656d76)); }

/* Links */
.cm-preview a {
  color: var(--cm-link, var(--link, #0969da));
  text-decoration: none;
}

.cm-preview a:hover {
  text-decoration: underline;
}

/* Inline styles */
.cm-preview mark {
  background-color: var(--cm-highlight, #fff8c5);
  padding: 0.1em 0.2em;
  border-radius: 3px;
}

.cm-preview del {
  color: var(--cm-muted, var(--muted, #656d76));
}
`;
var themeCSS = {
  githubDark,
  github,
  minimal,
  system
};

// src/themes/index.ts
var themeNames = {
  github: "github",
  githubDark: "github-dark",
  minimal: "minimal",
  system: "system"
};
function getThemePath(theme) {
  return `@create-markdown/preview/themes/${themeNames[theme]}.css`;
}

// src/plugins/shiki.ts
var DEFAULT_OPTIONS2 = {
  theme: "github-light",
  darkTheme: "github-dark",
  langs: [],
  lineNumbers: false,
  classPrefix: "cm-"
};
var highlighterPromise = null;
var highlighter = null;
function shikiPlugin(options) {
  const opts = { ...DEFAULT_OPTIONS2, ...options };
  return {
    name: "shiki",
    async init() {
      if (highlighter) return;
      try {
        const shikiModule = await import("shiki");
        const createHighlighter = shikiModule.createHighlighter;
        if (!createHighlighter) {
          console.warn("@create-markdown/preview: Shiki module loaded but createHighlighter not found");
          return;
        }
        highlighterPromise = createHighlighter({
          themes: [opts.theme, opts.darkTheme].filter(Boolean),
          langs: [
            "javascript",
            "typescript",
            "jsx",
            "tsx",
            "json",
            "html",
            "css",
            "markdown",
            "python",
            "rust",
            "go",
            "bash",
            "shell",
            ...opts.langs
          ]
        });
        highlighter = await highlighterPromise;
      } catch (error) {
        console.warn("@create-markdown/preview: Shiki not available. Install with: pnpm add shiki");
      }
    },
    renderBlock(block, _defaultRender) {
      if (block.type !== "codeBlock") {
        return null;
      }
      const codeBlock = block;
      const code = codeBlock.content.map((span) => span.text).join("");
      const language = codeBlock.props.language || "text";
      if (!highlighter) {
        return null;
      }
      try {
        const shikiHighlighter = highlighter;
        const html = shikiHighlighter.codeToHtml(code, {
          lang: language,
          theme: opts.theme
        });
        const prefix = opts.classPrefix;
        return `<div class="${prefix}code-block ${prefix}shiki" data-language="${language}">${html}</div>`;
      } catch (error) {
        return null;
      }
    },
    getCSS() {
      const prefix = opts.classPrefix;
      return `
.${prefix}shiki pre {
  padding: 16px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 0;
}

.${prefix}shiki code {
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace;
  font-size: 0.875em;
  background: transparent;
  padding: 0;
}

.${prefix}shiki .line {
  display: inline-block;
  width: 100%;
}

${opts.lineNumbers ? `
.${prefix}shiki pre {
  counter-reset: line;
}

.${prefix}shiki .line::before {
  counter-increment: line;
  content: counter(line);
  display: inline-block;
  width: 2em;
  margin-right: 1em;
  text-align: right;
  color: #6e7781;
  user-select: none;
}
` : ""}
`.trim();
    }
  };
}
var createShikiPlugin = shikiPlugin;

// src/plugins/mermaid.ts
var DEFAULT_OPTIONS3 = {
  theme: "default",
  config: {},
  classPrefix: "cm-",
  useUniqueIds: true
};
var mermaidModule = null;
var mermaidInitialized = false;
var diagramCounter = 0;
function mermaidPlugin(options) {
  const opts = { ...DEFAULT_OPTIONS3, ...options };
  return {
    name: "mermaid",
    async init() {
      if (mermaidInitialized) return;
      try {
        const mermaid = await import("mermaid");
        mermaidModule = mermaid.default || mermaid;
        const mermaidApi = mermaidModule;
        mermaidApi.initialize({
          startOnLoad: false,
          theme: opts.theme,
          securityLevel: "loose",
          ...opts.config
        });
        mermaidInitialized = true;
      } catch (error) {
        console.warn("@create-markdown/preview: Mermaid not available. Install with: pnpm add mermaid");
      }
    },
    renderBlock(block, _defaultRender) {
      if (block.type !== "codeBlock") {
        return null;
      }
      const codeBlock = block;
      const language = codeBlock.props.language?.toLowerCase();
      if (language !== "mermaid") {
        return null;
      }
      const code = codeBlock.content.map((span) => span.text).join("");
      const prefix = opts.classPrefix;
      const diagramId = opts.useUniqueIds ? `mermaid-${Date.now()}-${++diagramCounter}` : `mermaid-${++diagramCounter}`;
      return `
<div class="${prefix}mermaid-container">
  <pre class="${prefix}mermaid" id="${diagramId}">${escapeHtml2(code)}</pre>
</div>`.trim();
    },
    async postProcess(html) {
      if (!mermaidModule) {
        return html;
      }
      try {
        const mermaid = mermaidModule;
        const prefix = opts.classPrefix;
        const mermaidBlockRegex = new RegExp(
          `<pre class="${prefix}mermaid" id="([^"]+)">([\\s\\S]*?)</pre>`,
          "g"
        );
        const matches = [...html.matchAll(mermaidBlockRegex)];
        for (const match of matches) {
          const [fullMatch, id, escapedCode] = match;
          const code = unescapeHtml(escapedCode);
          try {
            const { svg } = await mermaid.render(id, code);
            html = html.replace(
              fullMatch,
              `<div class="${prefix}mermaid-diagram">${svg}</div>`
            );
          } catch (renderError) {
            console.warn(`Failed to render Mermaid diagram: ${renderError}`);
          }
        }
      } catch (error) {
        console.warn("Mermaid post-processing failed:", error);
      }
      return html;
    },
    getCSS() {
      const prefix = opts.classPrefix;
      return `
.${prefix}mermaid-container {
  margin-bottom: 16px;
  overflow-x: auto;
}

.${prefix}mermaid {
  background-color: transparent;
  text-align: center;
}

.${prefix}mermaid-diagram {
  display: flex;
  justify-content: center;
  padding: 16px;
  background-color: #f6f8fa;
  border-radius: 6px;
}

.${prefix}mermaid-diagram svg {
  max-width: 100%;
  height: auto;
}

/* Dark theme adjustments */
@media (prefers-color-scheme: dark) {
  .${prefix}mermaid-diagram {
    background-color: #161b22;
  }
}
`.trim();
    }
  };
}
var createMermaidPlugin = mermaidPlugin;
function escapeHtml2(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}
function unescapeHtml(text) {
  return text.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#039;/g, "'");
}

// src/web-component.ts
async function lazyParse2(markdown) {
  try {
    const core = await import("@create-markdown/core");
    return core.parse(markdown);
  } catch {
    throw new Error(
      "@create-markdown/core is required to parse markdown in <markdown-preview>. Install it, or provide pre-parsed blocks via the blocks attribute / setBlocks()."
    );
  }
}
var MarkdownPreviewElement = class extends HTMLElement {
  constructor() {
    super();
    __publicField(this, "_shadow", null);
    __publicField(this, "plugins", []);
    __publicField(this, "defaultTheme", "github");
    __publicField(this, "styleElement");
    __publicField(this, "contentElement");
    const mode = this.constructor._shadowMode;
    if (mode !== "none") {
      this._shadow = this.attachShadow({ mode });
    }
    this.styleElement = document.createElement("style");
    this.renderRoot.appendChild(this.styleElement);
    this.contentElement = document.createElement("div");
    this.contentElement.className = "markdown-preview-content";
    this.renderRoot.appendChild(this.contentElement);
    this.updateStyles();
  }
  static get observedAttributes() {
    return ["theme", "link-target", "async"];
  }
  get renderRoot() {
    return this._shadow ?? this;
  }
  connectedCallback() {
    this.render();
  }
  attributeChangedCallback(_name, _oldValue, _newValue) {
    this.render();
  }
  setPlugins(plugins) {
    this.plugins = plugins;
    this.render();
  }
  setDefaultTheme(theme) {
    this.defaultTheme = theme;
    this.render();
  }
  getMarkdown() {
    const blocksAttr = this.getAttribute("blocks");
    if (blocksAttr) {
      try {
        const blocks = JSON.parse(blocksAttr);
        return blocks.map((b) => b.content.map((s) => s.text).join("")).join("\n\n");
      } catch {
        return "";
      }
    }
    return this.textContent || "";
  }
  setMarkdown(markdown) {
    this.textContent = markdown;
    this.render();
  }
  setBlocks(blocks) {
    this.setAttribute("blocks", JSON.stringify(blocks));
    this.render();
  }
  getOptions() {
    const theme = this.getAttribute("theme") || this.defaultTheme;
    const linkTarget = this.getAttribute("link-target") || "_blank";
    return {
      theme,
      linkTarget,
      plugins: this.plugins
    };
  }
  async getBlocks() {
    const blocksAttr = this.getAttribute("blocks");
    if (blocksAttr) {
      try {
        return JSON.parse(blocksAttr);
      } catch {
        console.warn("Invalid blocks JSON in markdown-preview element");
        return [];
      }
    }
    const markdown = this.textContent || "";
    return lazyParse2(markdown);
  }
  async render() {
    const blocks = await this.getBlocks();
    const options = this.getOptions();
    const isAsync = this.hasAttribute("async") || this.plugins.length > 0;
    try {
      let html;
      if (isAsync) {
        html = await renderAsync(blocks, options);
      } else {
        html = blocksToHTML(blocks, options);
      }
      this.contentElement.innerHTML = html;
    } catch (error) {
      console.error("Error rendering markdown preview:", error);
      this.contentElement.innerHTML = `<div class="error">Error rendering content</div>`;
    }
  }
  updateStyles() {
    const pluginCSS = this.plugins.filter((p) => p.getCSS).map((p) => p.getCSS()).join("\n\n");
    const hostRule = this._shadow ? ":host { display: block; }" : "markdown-preview { display: block; }";
    this.styleElement.textContent = `
${hostRule}

.markdown-preview-content {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans', Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.6;
}

.error {
  color: #cf222e;
  padding: 1rem;
  background: #ffebe9;
  border-radius: 6px;
}

${pluginCSS}
    `.trim();
  }
};
/** @internal */
__publicField(MarkdownPreviewElement, "_shadowMode", "open");
var globalPlugins = [];
var globalDefaultTheme = "github";
function registerPreviewElement(options) {
  const tagName = options?.tagName || "markdown-preview";
  const plugins = options?.plugins || [];
  const defaultTheme = options?.defaultTheme || "github";
  const shadowMode = options?.shadowMode ?? "open";
  globalPlugins = plugins;
  globalDefaultTheme = defaultTheme;
  if (!customElements.get(tagName)) {
    class ConfiguredMarkdownPreview extends MarkdownPreviewElement {
      constructor() {
        super();
        this.setPlugins(globalPlugins);
        this.setDefaultTheme(globalDefaultTheme);
      }
    }
    __publicField(ConfiguredMarkdownPreview, "_shadowMode", shadowMode);
    customElements.define(tagName, ConfiguredMarkdownPreview);
  }
}
function autoRegister() {
  if (typeof window !== "undefined" && typeof customElements !== "undefined") {
    registerPreviewElement();
  }
}

// src/index.ts
var VERSION = "2.0.0";
export {
  MarkdownPreviewElement,
  VERSION,
  applyPreviewTheme,
  autoRegister,
  blocksToHTML,
  createMermaidPlugin,
  createShikiPlugin,
  getThemePath,
  markdownToHTML,
  mermaidPlugin,
  registerPreviewElement,
  renderAsync,
  shikiPlugin,
  themeNames,
  themeCSS as themes
};
//# sourceMappingURL=index.js.map